import React, { useState } from 'react';
import { supabase } from '../supabaseClient';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { LogIn, UserPlus, Zap, Trophy, AlertCircle, CheckCircle2 } from 'lucide-react';

export default function Login({ onDemoLogin }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter both email and password.');
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage(error.error_description || error.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (!email.trim() || !password.trim()) {
      setErrorMessage('Please enter both email and password.');
      return;
    }

    try {
      setLoading(true);
      const redirectTo = `${window.location.origin}`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo
        }
      });
      if (error) throw error;

      if (data?.session) {
        setSuccessMessage('Account created and logged in!');
      } else {
        setSuccessMessage('Account created! Check your email to confirm registration or try logging in.');
      }
    } catch (error) {
      console.error('Sign up error:', error);
      setErrorMessage(error.error_description || error.message || 'Sign up failed.');
    } finally {
      setLoading(false);
    }
  };

  const isLocalEnv =
    process.env.NODE_ENV === 'development' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  return (
    <div className="flex justify-center items-center min-h-screen bg-background p-4">
      <Card className="max-w-md w-full border shadow-xl">
        <CardHeader className="text-center pb-4">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Trophy className="h-6 w-6" />
          </div>
          <CardTitle className="text-2xl font-bold">Soccer Stat Tracker</CardTitle>
          <CardDescription>
            Sign in to manage seasons, roster, and game statistics
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {errorMessage && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          {successMessage && (
            <div className="p-3 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase text-muted-foreground">
                Email
              </label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase text-muted-foreground">
                Password
              </label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2">
                <LogIn className="w-4 h-4" />
                {loading ? 'Processing...' : 'Login'}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={handleSignUp}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2"
              >
                <UserPlus className="w-4 h-4" />
                Sign Up
              </Button>
            </div>
          </form>

          {isLocalEnv && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground font-medium">
                    Or Local Development
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={onDemoLogin}
                className="w-full flex items-center justify-center gap-2 font-semibold border-emerald-500/50 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10"
              >
                <Zap className="w-4 h-4 text-emerald-500" />
                Bypass / Continue as Local Demo User
              </Button>
            </>
          )}
        </CardContent>

        {isLocalEnv && (
          <CardFooter className="justify-center border-t pt-4">
            <p className="text-center text-xs text-muted-foreground">
              Tip: For local testing without PROD auth, use Local Demo mode.
            </p>
          </CardFooter>
        )}
      </Card>
    </div>
  );
}
